import {
  FontIcon,
  FontIcons,
  brandfolderColorPalette,
} from '@brandfolder/react';
import { Trans } from '@lingui/macro';
import React, {
  FormEvent,
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import styled from 'styled-components';

const MAX_NAME_LENGTH = 50;
const NAME_VALIDATION_REGEX = /[^a-zA-Z\s\-'\.]/g;

const PersonCardWrapper = styled.div`
  box-shadow: 1px 2px 6px 0 rgba(0, 0, 0, 0.09);
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .default-image {
    background-color: ${brandfolderColorPalette.neutrals[200]};
    height: calc(100% - 52px);
  }
`;

const ImageWrapper = styled.div`
  display: flex;
  position: relative;
  width: 100%;
`;

const LabelWrapper = styled.div`
  align-items: center;
  display: flex;
  height: 52px;
  padding: 0 16px;

  .name-input-container {
    width: 100%;

    input {
      height: 28px;
      min-height: 28px;
      padding: 8px 4px;
      width: 100%;
      border: 1px solid ${brandfolderColorPalette.neutrals[300]};
      border-radius: 4px;
      font-size: 14px;

      &:focus {
        outline: none;
        border-color: #4462c9;
      }
    }
  }
`;

const CardImage = styled.img`
  height: 210px;
  object-fit: cover;
  width: 100%;
`;

const DefaultImage = styled.div`
  align-items: center;
  color: ${brandfolderColorPalette.neutrals[500]};
  display: flex;
  height: 210px;
  justify-content: center;
  background-color: ${brandfolderColorPalette.neutrals[200]};
`;

const ClickableLabel = styled.div<{ $isPlaceholder?: boolean }>`
  color: ${({ $isPlaceholder }): string =>
    $isPlaceholder
      ? brandfolderColorPalette.primary[600]
      : brandfolderColorPalette.neutrals[700]
};
  font-size: 14px;
  font-weight: 450;
  line-height: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  display: ${({ $isPlaceholder }): string => $isPlaceholder ? 'flex' : 'block'};
  align-items: ${({ $isPlaceholder }): string => $isPlaceholder ? 'center' : 'initial'};
  gap: ${({ $isPlaceholder }): string => $isPlaceholder ? '8px' : '0'};

  &:hover {
    opacity: 0.8;
  }

  &:focus {
    outline: 2px solid #4462c9;
    outline-offset: 2px;
  }
`;

export interface PersonCardProps {
  imageUrl?: string;
  name?: string;
  onNameChange?: (name: string) => void;
}

const useImageError = (imageUrl?: string): { hasImageError: boolean; handleImageError: () => void } => {
  const [hasImageError, setHasImageError] = useState(false);

  useEffect(() => {
    setHasImageError(false);
  }, [imageUrl]);

  const handleImageError = useCallback(() => {
    setHasImageError(true);
  }, []);

  return { hasImageError, handleImageError };
};

const useNameInput = (initialName?: string, onNameChange?: (name: string) => void): {
  personName: string;
  showNameField: boolean;
  inputValue: string;
  formRef: React.RefObject<HTMLFormElement>;
  inputRef: React.RefObject<HTMLInputElement>;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
  handleBlur: (e: React.FocusEvent) => void;
  handleShowNameField: () => void;
  handleKeyDown: (e: React.KeyboardEvent) => void;
} => {
  const [personName, setPersonName] = useState(initialName || '');
  const [showNameField, setShowNameField] = useState(false);
  const [inputValue, setInputValue] = useState(initialName || '');

  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const newName = initialName || '';
    setPersonName(newName);
    setInputValue(newName);
  }, [initialName]);

  // Auto-focus input when field is shown
  useEffect(() => {
    if (showNameField && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showNameField]);

  const validateAndSanitizeName = useCallback((inputName: string): string => {
    const sanitized = inputName.replace(NAME_VALIDATION_REGEX, '');
    return sanitized.slice(0, MAX_NAME_LENGTH);
  }, []);

  const saveName = useCallback((value: string) => {
    const trimmedValue = value.trim();
    if (trimmedValue) {
      setPersonName(trimmedValue);
      onNameChange?.(trimmedValue);
    }
    setShowNameField(false);
    setInputValue(trimmedValue);
  }, [onNameChange]);

  const cancelEdit = useCallback(() => {
    setShowNameField(false);
    setInputValue(personName);
  }, [personName]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedValue = validateAndSanitizeName(e.target.value);
    setInputValue(sanitizedValue);
  }, [validateAndSanitizeName]);

  const handleSubmit = useCallback((e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmedValue = inputValue.trim();
    if (trimmedValue) {
      saveName(trimmedValue);
    } else {
      cancelEdit();
    }
  }, [inputValue, saveName, cancelEdit]);

  const handleBlur = useCallback((e: React.FocusEvent) => {
    // Don't close if focus is moving within the form
    if (formRef.current?.contains(e.relatedTarget as Node)) {
      return;
    }

    const trimmedValue = inputValue.trim();
    if (trimmedValue) {
      saveName(trimmedValue);
    } else {
      cancelEdit();
    }
  }, [inputValue, saveName, cancelEdit]);

  const handleShowNameField = useCallback(() => {
    setShowNameField(true);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleShowNameField();
    }
  }, [handleShowNameField]);

  return {
    personName,
    showNameField,
    inputValue,
    formRef,
    inputRef,
    handleInputChange,
    handleSubmit,
    handleBlur,
    handleShowNameField,
    handleKeyDown,
  };
};

export const PersonCard: FunctionComponent<PersonCardProps> = ({
  imageUrl,
  name,
  onNameChange
}) => {
  const { hasImageError, handleImageError } = useImageError(imageUrl);
  const {
    personName,
    showNameField,
    inputValue,
    formRef,
    inputRef,
    handleInputChange,
    handleSubmit,
    handleBlur,
    handleShowNameField,
    handleKeyDown,
  } = useNameInput(name, onNameChange);

  const shouldShowImage = useMemo(() =>
    imageUrl && !hasImageError,
  [imageUrl, hasImageError]
  );

  const nameDisplayProps = useMemo(() => ({
    'aria-label': `Current name: ${personName}. Click to edit`,
    onClick: handleShowNameField,
    onKeyDown: handleKeyDown,
    role: 'button' as const,
    tabIndex: 0,
  }), [personName, handleShowNameField, handleKeyDown]);

  const placeholderProps = useMemo(() => ({
    'aria-label': 'Click to add a name for this person',
    onClick: handleShowNameField,
    onKeyDown: handleKeyDown,
    role: 'button' as const,
    tabIndex: 0,
  }), [handleShowNameField, handleKeyDown]);

  return (
    <PersonCardWrapper>
      <ImageWrapper className={hasImageError ? 'default-image' : undefined}>
        {shouldShowImage ? (
          <CardImage
            alt="person"
            onError={handleImageError}
            src={imageUrl}
          />
        ) : (
          <DefaultImage>
            <FontIcon
              icon={FontIcons.UserFilled}
              iconSize={68}
              title="default image"
            />
          </DefaultImage>
        )}
      </ImageWrapper>

      <LabelWrapper className="person-name-label-wrapper">
        {personName && !showNameField && (
          <ClickableLabel {...nameDisplayProps}>
            {personName}
          </ClickableLabel>
        )}

        {!personName && !showNameField && (
          <ClickableLabel $isPlaceholder {...placeholderProps}>
            <FontIcon icon={FontIcons.Tag} iconSize={16} />
            <Trans>Name person</Trans>
          </ClickableLabel>
        )}

        {showNameField && (
          <div className="name-input-container">
            <form
              ref={formRef}
              onBlur={handleBlur}
              onSubmit={handleSubmit}
            >
              <input
                ref={inputRef}
                aria-describedby="name-input-help"
                aria-label="Enter person's name"
                maxLength={MAX_NAME_LENGTH}
                onChange={handleInputChange}
                placeholder="Enter name..."
                type="text"
                value={inputValue}
              />
            </form>
          </div>
        )}
      </LabelWrapper>
    </PersonCardWrapper>
  );
};
